import path from 'path';
import React from 'react';

import {wrapAddPanelIcon} from '@packages/systems/ui/shared-ui/wrapAddPanelIcon';
import {
  AddPanelModal64Icon,
  ElementDialogIcon as ModalIcon,
} from '@packages/systems/spring';
import {getFeatureFlagOutsideOfReact} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {Component, ElementPreset, Plugins} from '@packages/systems/wfdl';
import {EBoolean, EElement, EList, ERecord} from '@packages/systems/wfdl-ast';
import {iconName, qualify} from '@packages/systems/wfdl-Qualified';

import Modal from './Modal';
import {LabeledCheckboxType} from './LabeledCheckbox';

const ModalAddPanelIcon = wrapAddPanelIcon(AddPanelModal64Icon);

// Re-typed for the WFDL icon map, which rejects the `forwardRef` wrapper type.
const AddPanelModalIcon = (props: Plugins.IconProps): React.ReactElement =>
  React.createElement(ModalAddPanelIcon, props);

export const plugin = Plugins.PrivilegedPlugin({
  icons: {
    Modal: ModalIcon,
    AddPanelModal: AddPanelModalIcon,
  },
  atoms: {Modal},
  dataTypes: {LabeledCheckbox: LabeledCheckboxType},
  elementPresets: {
    Modal: ElementPreset.create({
      factory: EElement({
        id: 'Modal',
        type: qualify(Plugins.name('Modal'), Component.name('Modal')),
        data: ERecord({lockScroll: EBoolean(false), children: EList([])}),
      }),
      icon: {
        small: qualify(Plugins.name('Modal'), iconName('Modal')),
        medium: qualify(Plugins.name('Modal'), iconName('AddPanelModal')),
      },
      label: 'Modal',
      beta: {
        enabledBasedOnFlags: {
          featureFlagName: FEATURE_FLAGS.BUILDING_BLOCKS_POPOVERS,
        },
      },
      helpData: {
        description: [
          'A modal is a dialog that opens on top of the page and blocks it until closed.',
        ],
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/modal.4338a92fc1.mp4',
      },
      category: () =>
        getFeatureFlagOutsideOfReact(FEATURE_FLAGS.BUILDING_BLOCKS_POPOVERS)
          ? 'Advanced'
          : null,
    }),
  },

  // Bundled onto every page with a modal: the module holds the scroll lock and
  // opens modals in browsers without invoker commands, with or without IX3.
  siteModules: [
    {
      // No extension, for the reason given on Popover's `siteModules`: rspack
      // resolves `.ts` then `.js`, and deployed servers only have the swc `.js`.
      // Keep `webflow-modal.ts` to syntax every supported browser runs.
      filePaths: [path.resolve(__dirname, 'webflow-modal')],
      bundleIf: {
        type: 'node',
        test: ({type}) => type === 'Modal',
      },
    },
  ],
});
